import { PracticeManagementSystem } from '~graphql/generated/graphql'

/**
 * PMSes served through Bitwerx (AVImark via Bitwerx, Cornerstone, Impromed).
 * Mirrors `BITWERX_PMSES` on the server.
 */
export const BITWERX_PMSES: readonly PracticeManagementSystem[] = [
    PracticeManagementSystem.BwAvimark,
    PracticeManagementSystem.Cornerstone,
    PracticeManagementSystem.ImpromedInfinity,
]

export const BOOKING_ENTITY_CLINICIAN = 'Clinician'
export const BOOKING_ENTITY_ROOM = 'Room'
export const BOOKING_ENTITY_CLINICIAN_AND_ROOM = 'Clinician & Room'

export type BookingEntityOption =
    | typeof BOOKING_ENTITY_CLINICIAN
    | typeof BOOKING_ENTITY_ROOM
    | typeof BOOKING_ENTITY_CLINICIAN_AND_ROOM

export type PmsBookingEntityRules = {
    /** Options the clinic may choose. Avimark gets all three. */
    allowedOptions: readonly BookingEntityOption[]
    /** False when the PMS forces a single booking entity. */
    canEdit: boolean
    defaultUsesRooms: boolean
    defaultUsesClinicians: boolean
}

export const ALL_BOOKING_ENTITY_OPTIONS: readonly BookingEntityOption[] = [
    BOOKING_ENTITY_CLINICIAN,
    BOOKING_ENTITY_ROOM,
    BOOKING_ENTITY_CLINICIAN_AND_ROOM,
]

/**
 * PMSes whose API can search clients by name, phone or email, so we can offer a
 * live PIMS lookup. Mirrors the cloud controllers implementing
 * `lookupPimsClients` on the server; this only decides whether to show the
 * entry point, the server still rejects unsupported PMSes.
 *
 * ezyVet is listed because its official API supports search — clinics still on
 * the legacy integration get a server-side error explaining why.
 */
export const PIMS_LOOKUP_PMSES: readonly PracticeManagementSystem[] = [
    ...BITWERX_PMSES,
    PracticeManagementSystem.Shepherd,
    PracticeManagementSystem.Provet,
    PracticeManagementSystem.Pulse,
    PracticeManagementSystem.Rhapsody,
    PracticeManagementSystem.Daysmart,
    PracticeManagementSystem.NectarVet,
    PracticeManagementSystem.EzyVet,
]

export function supportsPimsLookup(
    pms: PracticeManagementSystem | null | undefined,
): boolean {
    return !!pms && PIMS_LOOKUP_PMSES.includes(pms)
}

export const isCloudPMS = (
    pms: PracticeManagementSystem | null | undefined,
): boolean => {
    if (!pms) return false

    switch (pms) {
        case PracticeManagementSystem.AvImark:
            return false
    }
    return true
}

export function isBitwerxPms(
    pms: PracticeManagementSystem | null | undefined,
): boolean {
    return !!pms && BITWERX_PMSES.includes(pms)
}

/**
 * Bitwerx-backed clinics get client address from the PMS, so the booking
 * "require new client address" switch is display-only.
 */
export function canEditNewClientAddressRequirement(
    pms: PracticeManagementSystem | null | undefined,
): boolean {
    return !isBitwerxPms(pms)
}

/**
 * Which scheduling resources a PMS may use.
 *
 * - Avimark (desktop + Bitwerx): clinician, room, or both — user may choose
 * - Impromed: rooms only
 * - Cornerstone: clinician and room
 * - Every other PMS: clinicians only
 */
export function getPmsBookingEntityRules(
    pms: PracticeManagementSystem | null | undefined,
): PmsBookingEntityRules {
    switch (pms) {
        case PracticeManagementSystem.AvImark:
        case PracticeManagementSystem.BwAvimark:
            return {
                allowedOptions: ALL_BOOKING_ENTITY_OPTIONS,
                canEdit: true,
                defaultUsesRooms: false,
                defaultUsesClinicians: true,
            }
        case PracticeManagementSystem.ImpromedInfinity:
            return {
                allowedOptions: [BOOKING_ENTITY_ROOM],
                canEdit: false,
                defaultUsesRooms: true,
                defaultUsesClinicians: false,
            }
        case PracticeManagementSystem.Cornerstone:
            return {
                allowedOptions: [BOOKING_ENTITY_CLINICIAN_AND_ROOM],
                canEdit: false,
                defaultUsesRooms: true,
                defaultUsesClinicians: true,
            }
        default:
            return {
                allowedOptions: [BOOKING_ENTITY_CLINICIAN],
                canEdit: false,
                defaultUsesRooms: false,
                defaultUsesClinicians: true,
            }
    }
}

/**
 * Resolves rooms/clinician flags for a PMS. Forced PMSes ignore stored values
 * so a stale setting cannot book with the wrong resource.
 */
export function resolveBookingEntityForPms(
    pms: PracticeManagementSystem | null | undefined,
    storedUsesRooms: boolean,
    storedUsesClinicians: boolean,
): { usesRooms: boolean; usesClinicians: boolean } {
    const rules = getPmsBookingEntityRules(pms)
    if (!pms || rules.canEdit) {
        return {
            usesRooms: storedUsesRooms,
            usesClinicians: storedUsesClinicians,
        }
    }

    return {
        usesRooms: rules.defaultUsesRooms,
        usesClinicians: rules.defaultUsesClinicians,
    }
}

/** True when the PMS always schedules with both rooms and clinicians. */
export function requiresRoomAndClinician(
    pms: PracticeManagementSystem | null | undefined,
): boolean {
    const rules = getPmsBookingEntityRules(pms)
    return rules.defaultUsesRooms && rules.defaultUsesClinicians
}
